import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { IMAGES } from "@/lib/brand";
import { AnimatedCounter } from "@/components/AnimatedCounter";

const EASE = [0.16, 1, 0.3, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
};

export const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 600], [0, 120]);
  const overlayY = useTransform(scrollY, [0, 600], [0, 60]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] overflow-hidden bg-[#0B1020] text-white"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y: bgY, backgroundImage: `url(${IMAGES.hero})` }}
        className="absolute inset-0 -top-[10%] bg-cover bg-center opacity-60"
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 gradient-hero" />
      {/* Grid pattern */}
      <motion.div style={{ y: overlayY }} className="absolute inset-0 grid-bg opacity-40" />
      {/* Glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[700px] w-[1000px] max-w-[140vw] -translate-x-1/2 rounded-full bg-[#7C3AED] opacity-25 blur-[140px]" />

      <div className="relative z-10 mx-auto flex min-h-[100vh] max-w-7xl flex-col justify-center px-5 pt-40 pb-16 md:px-8 md:pt-44 lg:pt-32">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.14, delayChildren: 0.1 } } }}
          className="max-w-4xl"
        >
          <motion.h1
            variants={fadeUp}
            className="font-display text-balance text-[clamp(1.875rem,5.5vw,4.5rem)] font-bold leading-[1.05] tracking-tight [overflow-wrap:break-word] hyphens-auto"
            lang="de"
            data-testid="hero-headline"
          >
            {t.hero.headline.split(" ").slice(0, -2).join(" ")}{" "}
            <span className="gradient-text">{t.hero.headline.split(" ").slice(-2).join(" ")}</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-2xl text-balance text-base leading-relaxed text-slate-300 md:text-lg"
          >
            {t.hero.subheadline}
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => go("contact")}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-semibold text-[#0F172A] shadow-glow-blue transition-all hover:scale-[1.03] hover:shadow-glow-purple"
            >
              {t.hero.primary_cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => go("contact")}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur-md transition-all hover:bg-white/10"
            >
              <Phone className="h-4 w-4" />
              {t.hero.secondary_cta}
            </button>
          </motion.div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
          }}
          className="mt-16 md:mt-24"
        >
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {t.hero.stats.map((s, i) => (
              <motion.div
                key={i}
                data-testid="hero-stat-item"
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
                }}
                whileHover={{ y: -4, transition: { duration: 0.3 } }}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-md transition-colors hover:border-[#7C3AED]/50 hover:bg-white/[0.07] md:p-6"
              >
                <div className="absolute inset-0 -z-10 opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#7C3AED]/30 blur-3xl" />
                </div>
                <div className="font-display text-3xl font-bold tracking-tight md:text-5xl">
                  <AnimatedCounter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.16em] text-slate-400 md:text-xs">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-background" />
    </section>
  );
};

export default Hero;
